import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

type ArticleCardProps = {
  article: Article;
  variant?: 'default' | 'wide' | 'compact';
};

const formatViews = (views: number): string =>
  views >= 10000 ? `${(views / 10000).toFixed(1)} 万` : `${views}`;

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <article className="flex gap-4 items-start group">
        <Link
          to={`/article/${article.slug}`}
          className="w-24 h-20 md:w-28 md:h-20 rounded-lg overflow-hidden shrink-0 bg-background-200"
          aria-label={article.title}
        >
          <img
            src={article.cover}
            alt={article.coverAlt}
            title={`${article.title} - 反差现场`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="min-w-0">
          <Link
            to={`/category/${article.category}`}
            className="text-xs text-primary-600 font-label hover:underline"
          >
            {article.categoryName}
          </Link>
          <h3 className="mt-1.5 text-sm md:text-[15px] font-medium leading-snug text-foreground-950 line-clamp-2">
            <Link to={`/article/${article.slug}`} className="hover:text-primary-600 transition-colors">
              {article.title}
            </Link>
          </h3>
          <p className="mt-2 text-xs text-foreground-500 font-label">
            {article.date} · 阅读 {article.readTime} 分钟
          </p>
        </div>
      </article>
    );
  }

  const wide = variant === 'wide';

  return (
    <article
      className={`group bg-background-50 border border-background-200 rounded-lg overflow-hidden flex ${
        wide ? 'flex-col lg:flex-row' : 'flex-col'
      }`}
    >
      <Link
        to={`/article/${article.slug}`}
        className={`block overflow-hidden bg-background-200 ${
          wide ? 'w-full lg:w-[46%] h-56 lg:h-auto' : 'w-full h-52'
        }`}
        aria-label={article.title}
      >
        <img
          src={article.cover}
          alt={article.coverAlt}
          title={`${article.title} - 反差现场`}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
      </Link>
      <div className={`flex flex-col p-5 md:p-6 ${wide ? 'w-full lg:w-[54%] justify-center' : 'flex-1'}`}>
        <div className="flex items-center gap-3 text-xs font-label">
          <Link
            to={`/category/${article.category}`}
            className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 hover:bg-secondary-200 transition-colors"
          >
            {article.categoryName}
          </Link>
          <span className="text-foreground-400">{article.date}</span>
        </div>
        <h3
          className={`mt-3 font-heading font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors ${
            wide ? 'text-lg md:text-2xl' : 'text-base md:text-lg'
          }`}
        >
          <Link to={`/article/${article.slug}`}>{article.title}</Link>
        </h3>
        <p
          className={`mt-3 text-sm leading-relaxed text-foreground-600 ${
            wide ? 'line-clamp-3' : 'line-clamp-2'
          }`}
        >
          {article.excerpt}
        </p>
        <div className="mt-4 flex items-center justify-between text-xs text-foreground-500 font-label">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line" aria-hidden="true" />
            {article.author}
          </span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" aria-hidden="true" />
              {formatViews(article.views)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-timer-line" aria-hidden="true" />
              {article.readTime} 分钟
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}